import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  TextField,
  Button,
  Rating,
  Chip,
  Paper,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  IconButton,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  LinearProgress
} from '@mui/material';
import {
  ArrowBack,
  PhotoCamera,
  Send
} from '@mui/icons-material';
import { dummyCampaigns } from '../utils/dummyData';

interface ReviewData {
  rating: number;
  title: string;
  content: string;
  pros: string[];
  cons: string[];
  images: File[];
  wouldRecommend: boolean;
}

const ReviewWritePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [reviewData, setReviewData] = useState<ReviewData>({
    rating: 0,
    title: '',
    content: '',
    pros: [''],
    cons: [''],
    images: [],
    wouldRecommend: false
  });

  const campaign = dummyCampaigns.find(c => c.id === id);

  if (!campaign) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          캠페인을 찾을 수 없습니다.
        </Alert>
      </Container>
    );
  }

  const steps = [
    {
      label: '기본 평가',
      description: '전반적인 만족도를 평가해주세요'
    },
    {
      label: '상세 리뷰',
      description: '자세한 경험을 공유해주세요'
    },
    {
      label: '장단점',
      description: '좋았던 점과 아쉬웠던 점을 알려주세요'
    },
    {
      label: '사진 첨부',
      description: '체험 사진을 업로드해주세요'
    },
    {
      label: '최종 확인',
      description: '작성한 리뷰를 확인하고 제출하세요'
    }
  ];

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateStep = (step: number) => {
    switch (step) {
      case 0:
        return reviewData.rating > 0;
      case 1:
        return reviewData.title.trim() !== '' && reviewData.content.trim() !== '';
      case 2:
        return reviewData.pros.some(pro => pro.trim() !== '') && 
               reviewData.cons.some(con => con.trim() !== '');
      case 3:
        return true; // 사진은 선택사항
      default:
        return true;
    }
  };

  const handleRatingChange = (value: number | null) => {
    setReviewData(prev => ({
      ...prev,
      rating: value || 0,
      wouldRecommend: (value || 0) >= 4
    }));
  };

  const handleInputChange = (field: keyof ReviewData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setReviewData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleArrayChange = (field: 'pros' | 'cons', index: number, value: string) => {
    setReviewData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const addArrayItem = (field: 'pros' | 'cons') => {
    setReviewData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const removeArrayItem = (field: 'pros' | 'cons', index: number) => {
    setReviewData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newImages = Array.from(files);
      setReviewData(prev => ({
        ...prev,
        images: [...prev.images, ...newImages].slice(0, 5) // 최대 5장
      }));
    }
  };

  const removeImage = (index: number) => {
    setReviewData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      // TODO: Firebase에 리뷰 저장 로직 구현
      console.log('Review submission:', reviewData);
      
      // 임시 제출 처리
      setTimeout(() => {
        setIsSubmitting(false);
        setActiveStep(steps.length);
        setTimeout(() => {
          navigate('/campaigns');
        }, 3000);
      }, 2000);
    } catch (error) {
      console.error('Review submission error:', error);
      setIsSubmitting(false);
    }
  };

  const getRatingText = (rating: number) => {
    if (rating >= 5) return '매우 만족';
    if (rating >= 4) return '만족';
    if (rating >= 3) return '보통';
    if (rating >= 2) return '불만족';
    return '매우 불만족';
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="h6" gutterBottom>
              전반적으로 얼마나 만족하셨나요?
            </Typography>
            <Rating
              size="large"
              value={reviewData.rating}
              onChange={(_, value) => handleRatingChange(value)}
              sx={{ my: 3, fontSize: '3rem' }}
            />
            <Typography variant="h6" color="primary" gutterBottom>
              {reviewData.rating > 0 && getRatingText(reviewData.rating)}
            </Typography>
            {reviewData.rating > 0 && (
              <Typography variant="body2" color="text.secondary">
                {reviewData.wouldRecommend ? '다른 사람에게 추천하고 싶어요!' : '경험을 공유해주세요.'}
              </Typography>
            )}
          </Box>
        );

      case 1:
        return (
          <Box sx={{ py: 2 }}>
            <TextField
              fullWidth
              label="리뷰 제목"
              value={reviewData.title}
              onChange={handleInputChange('title')}
              margin="normal"
              required
              placeholder="한 줄로 경험을 요약해주세요"
              helperText="예: 정말 만족스러운 제품이었어요!"
            />
            <TextField
              fullWidth
              label="상세 리뷰"
              value={reviewData.content}
              onChange={handleInputChange('content')}
              margin="normal"
              required
              multiline
              rows={6}
              placeholder="자세한 경험담을 들려주세요. 다른 사용자들에게 도움이 될 정보를 포함해주세요."
              helperText="최소 50자 이상 작성해주세요"
            />
            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                작성된 글자 수: {reviewData.content.length}자
              </Typography>
              {reviewData.content.length >= 50 && (
                <Send color="success" fontSize="small" />
              )}
            </Box>
          </Box>
        );

      case 2:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              좋았던 점
            </Typography>
            {reviewData.pros.map((pro, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  fullWidth
                  label={`장점 ${index + 1}`}
                  value={pro}
                  onChange={(e) => handleArrayChange('pros', index, e.target.value)}
                  placeholder="좋았던 점을 입력해주세요"
                />
                {reviewData.pros.length > 1 && (
                  <IconButton onClick={() => removeArrayItem('pros', index)}>
                    <Send />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              variant="outlined"
              onClick={() => addArrayItem('pros')}
              sx={{ mb: 3 }}
              disabled={reviewData.pros.length >= 5}
            >
              장점 추가
            </Button>

            <Typography variant="h6" gutterBottom>
              아쉬웠던 점
            </Typography>
            {reviewData.cons.map((con, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  fullWidth
                  label={`단점 ${index + 1}`}
                  value={con}
                  onChange={(e) => handleArrayChange('cons', index, e.target.value)}
                  placeholder="아쉬웠던 점을 입력해주세요"
                />
                {reviewData.cons.length > 1 && (
                  <IconButton onClick={() => removeArrayItem('cons', index)}>
                    <Send />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button
              variant="outlined"
              onClick={() => addArrayItem('cons')}
              disabled={reviewData.cons.length >= 5}
            >
              단점 추가
            </Button>
          </Box>
        );

      case 3:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              체험 사진 업로드
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              실제 체험한 사진을 업로드해주세요. (최대 5장, 선택사항)
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCamera />}
                sx={{ alignSelf: 'flex-start' }}
                disabled={reviewData.images.length >= 5}
              >
                사진 선택
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                />
              </Button>

              {reviewData.images.length > 0 && (
                <ImageList sx={{ width: '100%', maxHeight: 400 }} cols={3} rowHeight={164}>
                  {reviewData.images.map((image, index) => (
                    <ImageListItem key={index}>
                      <img
                        src={URL.createObjectURL(image)}
                        alt={`업로드 이미지 ${index + 1}`}
                        loading="lazy"
                      />
                      <ImageListItemBar
                        actionIcon={
                          <IconButton
                            sx={{ color: 'rgba(255, 255, 255, 0.54)' }}
                            onClick={() => removeImage(index)}
                          >
                            <Send />
                          </IconButton>
                        }
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              )}
            </Box>
          </Box>
        );

      case 4:
        return (
          <Box sx={{ py: 2 }}>
            <Typography variant="h6" gutterBottom>
              작성한 리뷰 확인
            </Typography>
            
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Rating value={reviewData.rating} readOnly />
                <Typography variant="body1" sx={{ ml: 1 }}>
                  {getRatingText(reviewData.rating)}
                </Typography>
              </Box>
              
              <Typography variant="h6" gutterBottom>
                {reviewData.title}
              </Typography>
              
              <Typography variant="body1" paragraph>
                {reviewData.content}
              </Typography>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="success.main" gutterBottom>
                  좋았던 점
                </Typography>
                {reviewData.pros.filter(pro => pro.trim()).map((pro, index) => (
                  <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                    • {pro}
                  </Typography>
                ))}
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" color="error.main" gutterBottom>
                  아쉬웠던 점
                </Typography>
                {reviewData.cons.filter(con => con.trim()).map((con, index) => (
                  <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                    • {con}
                  </Typography>
                ))}
              </Box>

              {reviewData.images.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    첨부 사진 ({reviewData.images.length}장)
                  </Typography>
                  <ImageList sx={{ width: '100%', maxHeight: 200 }} cols={4} rowHeight={100}>
                    {reviewData.images.map((image, index) => (
                      <ImageListItem key={index}>
                        <img
                          src={URL.createObjectURL(image)}
                          alt={`리뷰 이미지 ${index + 1}`}
                          loading="lazy"
                        />
                      </ImageListItem>
                    ))}
                  </ImageList>
                </Box>
              )}
            </Paper>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                • 리뷰 제출 후 관리자 검토를 거쳐 승인됩니다.<br/>
                • 승인 완료 시 {campaign.reward.points.toLocaleString()}P가 지급됩니다.<br/>
                • 허위 리뷰 작성 시 계정 제재를 받을 수 있습니다.
              </Typography>
            </Alert>
          </Box>
        );

      default:
        return null;
    }
  };

  // 완료 페이지
  if (activeStep === steps.length) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Send sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom>
          리뷰 제출 완료!
        </Typography>
        <Typography variant="body1" color="text.secondary" gutterBottom>
          소중한 리뷰를 작성해주셔서 감사합니다.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          관리자 검토 후 승인되면 {campaign.reward.points.toLocaleString()}P가 지급됩니다.
        </Typography>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          잠시 후 캠페인 목록으로 이동합니다...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* 뒤로가기 버튼 */}
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        뒤로가기
      </Button>

      {/* 캠페인 정보 */}
      <Card sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex' }}>
          <CardMedia
            component="img"
            sx={{ width: 120, height: 120 }}
            image={campaign.imageUrl}
            alt={campaign.title}
          />
          <CardContent sx={{ flex: 1 }}>
            <Typography variant="h6" gutterBottom>
              {campaign.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {campaign.brand}
            </Typography>
            <Chip
              label={`${campaign.reward.points.toLocaleString()}P 지급`}
              color="primary"
              size="small"
            />
          </CardContent>
        </Box>
      </Card>

      {/* 리뷰 작성 폼 */}
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            리뷰 작성하기
          </Typography>
          
          <Stepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label}>
                <StepLabel>
                  <Typography variant="h6">{step.label}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepLabel>
                <StepContent>
                  {getStepContent(index)}
                  <Box sx={{ mb: 2 }}>
                    <div>
                      {index === steps.length - 1 ? (
                        <Button
                          variant="contained"
                          onClick={handleSubmit}
                          disabled={isSubmitting}
                          sx={{ mt: 1, mr: 1 }}
                        >
                          {isSubmitting ? '제출 중...' : '리뷰 제출'}
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          onClick={handleNext}
                          disabled={!validateStep(index)}
                          sx={{ mt: 1, mr: 1 }}
                        >
                          다음
                        </Button>
                      )}
                      <Button
                        disabled={index === 0}
                        onClick={handleBack}
                        sx={{ mt: 1, mr: 1 }}
                      >
                        이전
                      </Button>
                    </div>
                  </Box>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ReviewWritePage; 